import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, ChevronRight, PlayCircle, Clock, ClipboardList, Search, X, RefreshCw, AlertTriangle, Lock, CircleCheck, CalendarDays } from "lucide-react";
import { API } from "../contexts/AuthContext";
import BatchAccessGate from "../components/BatchAccessGate";
import CheckinGateScreen from "../components/CheckinGateScreen";
import StudyLoader from "../components/StudyLoader";
import "../lms.css";

// Prefetch the lazy lecture chunk so the click-through feels instant.
// Invoked on mouseenter / touchstart of any "Watch" link — by the time the
// student commits to clicking, webpack already has the bundle in cache.
let _lecturePrefetchPromise = null;
const prefetchLectureChunk = () => {
  if (_lecturePrefetchPromise) return _lecturePrefetchPromise;
  _lecturePrefetchPromise = import("./StudentLecture").catch(() => { _lecturePrefetchPromise = null; });
  return _lecturePrefetchPromise;
};

export default function StudentStudy() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Persist navigation state in the URL — when the student leaves for a
  // lecture and hits Back, they land exactly where they were (same subject
  // open, same chapter expanded), not collapsed at the top.
  const urlSubject = searchParams.get("subject") || null;
  const urlChapter = searchParams.get("chapter") || null;
  const urlBatch = searchParams.get("batch") || null;

  const [tree, setTree] = useState([]);
  const [batches, setBatches] = useState([]);
  const [activeBatchId, setActiveBatchId] = useState(null);
  // Iter 193.2 — when the active batch has an unmet check-in gate,
  // backend returns 200 with `gated: {code, batch_id, batch_name}`
  // and an empty tree so the frontend can render the batch picker
  // alongside the gate screen. Students can then switch to an
  // ungated batch without being globally locked out.
  const [gatedInfo, setGatedInfo] = useState(null);
  // Soft state — `loading` only governs the initial skeleton. Batch switches
  // run in the background and only swap the data when ready, so the page
  // never flashes back to a skeleton mid-session.
  const [loading, setLoading] = useState(true);
  const [batchSwitching, setBatchSwitching] = useState(false);
  // Iter 193.3 — remember which batch's name to surface in the progress
  // overlay so the student sees "Loading Biology Batch…" instead of
  // "Loading your next batch…" when they've tapped a specific pill.
  const [switchingTo, setSwitchingTo] = useState(null);
  const [err, setErr] = useState(null);
  const [openSubject, setOpenSubjectState] = useState(urlSubject);
  const [openChapter, setOpenChapterState] = useState(urlChapter);
  const [q, setQ] = useState("");

  // Wrap the open-subject / open-chapter setters so the URL stays in sync.
  // Using `replace: true` keeps the back-button stack clean: pressing Back
  // from a lecture still returns to the previous *page*, not through every
  // intermediate expand/collapse.
  const setOpenSubject = useCallback((id) => {
    setOpenSubjectState(id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set("subject", id); else next.delete("subject");
      // Collapse the chapter param when subject changes.
      next.delete("chapter");
      return next;
    }, { replace: true });
    setOpenChapterState(null);
  }, [setSearchParams]);
  const setOpenChapter = useCallback((id) => {
    setOpenChapterState(id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (id) next.set("chapter", id); else next.delete("chapter");
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const load = useCallback(async (batchIdToLoad, { silent = false } = {}) => {
    // `silent` swaps the data without flashing the full-page skeleton —
    // used for batch switches so the student stays oriented.
    if (silent) setBatchSwitching(true);
    else setLoading(true);
    setErr(null);
    // Hard timeout — if anything (auth retry, slow DB) hangs longer than 25s
    // we flip out of the shimmer state and show a retry button. Better to
    // surface the issue than leave the user staring at a skeleton forever.
    const TIMEOUT_MS = 25000;
    let timedOut = false;
    const timer = setTimeout(() => {
      timedOut = true;
      if (silent) setBatchSwitching(false);
      else setLoading(false);
      setErr("Took too long to load your courses. Tap retry — your network may be slow.");
    }, TIMEOUT_MS);
    try {
      const params = batchIdToLoad ? { batch_id: batchIdToLoad } : {};
      const r = await axios.get(`${API}/me/content-tree`, { params, timeout: TIMEOUT_MS });
      if (timedOut) return; // timeout already fired — ignore late response
      // Backend now returns { batches, active_batch_id, tree, gated? }.
      // Keep backwards compat with array-only response just in case.
      const data = r.data;
      const t = Array.isArray(data) ? data : (data?.tree || []);
      const bs = Array.isArray(data) ? [] : (data?.batches || []);
      const abi = Array.isArray(data) ? null : data?.active_batch_id;
      const gated = Array.isArray(data) ? null : (data?.gated || null);
      setTree(t);
      setBatches(bs);
      setActiveBatchId(abi);
      setGatedInfo(gated);
      // Restore the previously-open subject from URL if it still exists in
      // the new tree, otherwise fall back to the first subject so the page
      // is never blank.
      const stillOpen = t.find((s) => s.id === urlSubject);
      if (stillOpen) setOpenSubjectState(stillOpen.id);
      else if (t[0]) setOpenSubjectState(t[0].id);
      else setOpenSubjectState(null);
    } catch (e) {
      if (timedOut) return;
      // Iter 193 — content-tree returns 423 with code BATCH_RESTRICTED
      // for admin-imposed hard blocks. Swallow those silently:
      // BatchAccessGate below renders the restriction takeover.
      const detail = e.response?.data?.detail;
      const code = detail && typeof detail === "object" ? detail.code : null;
      if (e.response?.status === 423 && code === "BATCH_RESTRICTED") {
        setTree([]);
        setGatedInfo(null);
        if (detail.batch_id) setActiveBatchId(detail.batch_id);
        else if (batchIdToLoad) setActiveBatchId(batchIdToLoad);
        return;
      }
      const msg = detail
        || e.message
        || "Could not load your courses. Please retry.";
      setErr(typeof msg === "string" ? msg : "Could not load your courses. Please retry.");
      setTree([]);
    } finally {
      clearTimeout(timer);
      if (!timedOut) {
        if (silent) {
          setBatchSwitching(false);
          // Let the progress overlay finish its 100% completion sweep
          // before clearing the label so students see the reassuring
          // "100% — Biology Batch" flash even on fast networks.
          setTimeout(() => setSwitchingTo(null), 380);
        } else {
          setLoading(false);
        }
      }
    }
  }, [urlSubject]);

  // First-mount load — honour `?batch=` in the URL so deep-links / back-
  // navigation restore the chosen batch. Runs once on mount and again
  // only when the URL batch param changes (e.g., ?batch=X switch).
  // Do NOT depend on `load` — its identity flips whenever `urlSubject`
  // changes (user picking a subject in the tree), and rerunning here
  // would refetch the entire content tree on every subject click.
  useEffect(() => { load(urlBatch); }, [urlBatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Global search — matches chapter/topic/lecture names across subjects.
  const query = q.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!query) return null;
    const out = [];
    tree.forEach(s => (s.chapters || []).forEach(c => {
      if ((c.name || "").toLowerCase().includes(query)) out.push({ type: "chapter", s, c });
      (c.topics || []).forEach(t => {
        if ((t.name || "").toLowerCase().includes(query)) out.push({ type: "topic", s, c, t });
        (t.lectures || []).forEach(l => {
          if ((l.title || "").toLowerCase().includes(query)) out.push({ type: "lecture", s, c, t, l });
        });
      });
    }));
    return out.slice(0, 40);
  }, [tree, query]);

  if (loading) return (
    <BatchAccessGate batchId={activeBatchId || urlBatch || null}>
      <div className="max-w-6xl mx-auto px-5 md:px-10 py-8 md:py-10" data-testid="study-loading">
        <StudyLoader active={true} variant="initial" />
      </div>
    </BatchAccessGate>
  );
  if (err) {
    return (
      <BatchAccessGate batchId={activeBatchId || urlBatch || null}>
        <div className="max-w-3xl mx-auto p-10">
          <div className="portal-empty" style={{ background: "#fef2f2", border: "1.5px solid #dc2626" }}>
            <AlertTriangle size={22} className="mx-auto mb-3" style={{ color: "#dc2626" }} />
            <div className="text-lg font-semibold" style={{ color: "#450a0a" }}>Couldn&apos;t load your courses</div>
            <p className="text-sm mt-1 mb-4" style={{ color: "#7f1d1d" }}>{err}</p>
            <button
              onClick={load}
              className="portal-btn portal-btn-primary inline-flex items-center gap-2"
              data-testid="study-retry"
            >
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        </div>
      </BatchAccessGate>
    );
  }
  if (tree.length === 0 && !gatedInfo) {
    return (
      <BatchAccessGate batchId={activeBatchId || urlBatch || null}>
        <div className="max-w-3xl mx-auto p-10">
          <div className="portal-empty">
            <BookOpen size={22} className="mx-auto mb-3" style={{ color: "var(--px-ink-faint)" }} />
            <div className="text-lg font-semibold" style={{ color: "var(--px-ink)" }}>No content yet</div>
            <p className="text-sm mt-1">Your admin is uploading lectures. Check back in a bit.</p>
          </div>
        </div>
      </BatchAccessGate>
    );
  }

  const subject = tree.find((s) => s.id === openSubject) || tree[0];

  return (
    <BatchAccessGate batchId={activeBatchId}>
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-8 md:py-10">
      <div className="portal-page-header">
        <div>
          <div className="portal-kicker">— Study</div>
          <h1 className="portal-page-title">Your learning universe</h1>
          <p className="portal-page-sub">Browse by subject, search a concept, or jump straight into MCQ practice.</p>
        </div>
      </div>

      {/* Multi-batch picker — renders when the student has 2+ active
          enrollments, OR when a single-batch student has a schedule
          URL (so the Schedule button surfaces regardless). Switching
          loads the per-batch curriculum (no leak across batches).
          Iter 193.2 — each pill shows a lock chip when its batch has an
          unmet check-in gate, so the student sees the restriction at
          selection time without opening the batch first.
          Iter 193.7 — an inline Schedule button now sits next to each
          pill when the admin has set a `schedule_url` for that batch. */}
      {(batches.length > 1 || batches.some((b) => b.schedule_url)) && (
        <div className="mb-5" data-testid="study-batch-picker">
          <div className="portal-kicker mb-2">
            — {batches.length > 1 ? `Switch batch (${batches.length})` : "Your batch"}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            {batches.map((b) => {
              const active = b.id === activeBatchId;
              const gated = b.is_checkin_gated && b.checked_in_today === false;
              const cleared = b.is_checkin_gated && b.checked_in_today === true;
              const restricted = b.is_restricted;
              return (
                <div
                  key={b.id}
                  className="inline-flex items-center rounded-full"
                  style={{
                    // Grouped pill container — pill + schedule button visually joined.
                    background: b.schedule_url ? "transparent" : undefined,
                  }}
                  data-testid={`study-batch-group-${b.id}`}
                >
                <button
                  onClick={() => {
                    if (active || batchSwitching) return;
                    setSwitchingTo(b.name);
                    setSearchParams((prev) => {
                      const next = new URLSearchParams(prev);
                      next.set("batch", b.id);
                      next.delete("subject");
                      next.delete("chapter");
                      return next;
                    }, { replace: true });
                    load(b.id, { silent: true });
                  }}
                  disabled={batchSwitching && !active}
                  title={
                    restricted ? "You have been restricted from this batch"
                      : gated ? `Locked — complete today's check-in for "${b.name}" to unlock`
                      : cleared ? "Check-in complete — content unlocked"
                      : ""
                  }
                  className={
                    "inline-flex items-center gap-1.5 pl-3 h-9 text-xs font-semibold transition disabled:opacity-60 max-w-full " +
                    (b.schedule_url ? "rounded-l-full pr-2.5" : "rounded-full pr-3")
                  }
                  style={{
                    background: active
                      ? (gated ? "linear-gradient(135deg, #a855f7, #7c3aed)"
                        : restricted ? "linear-gradient(135deg, #dc2626, #991b1b)"
                        : "var(--accent-deep)")
                      : "var(--px-paper-soft)",
                    color: active ? "#ffffff" : (gated ? "#7c3aed" : (restricted ? "#b91c1c" : "var(--px-ink)")),
                    border: `1.5px solid ${
                      active
                        ? (gated ? "#7c3aed" : restricted ? "#991b1b" : "var(--accent-deep)")
                        : (gated ? "#c4b5fd" : restricted ? "#fca5a5" : "var(--px-line-strong)")
                    }`,
                    borderRight: b.schedule_url
                      ? (active ? "1.5px solid rgba(255,255,255,0.35)" : "1.5px solid var(--px-line-strong)")
                      : undefined,
                    minWidth: 0,
                  }}
                  data-testid={`study-batch-pill-${b.id}`}
                >
                  {gated && (
                    <Lock size={11} strokeWidth={2.5} className="shrink-0" data-testid={`study-batch-pill-${b.id}-lock`} />
                  )}
                  {cleared && !restricted && (
                    <CircleCheck size={11} strokeWidth={2.5} className="shrink-0" style={{ color: active ? "#bbf7d0" : "#16a34a" }} />
                  )}
                  {restricted && !gated && (
                    <Lock size={11} strokeWidth={2.5} className="shrink-0" data-testid={`study-batch-pill-${b.id}-restricted`} />
                  )}
                  <span
                    className="truncate"
                    style={{ maxWidth: 180, display: "inline-block", verticalAlign: "middle" }}
                    title={b.name}
                  >
                    {active && batchSwitching ? "Loading…" : b.name}
                  </span>
                  {gated && (
                    <span
                      className="ml-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide whitespace-nowrap shrink-0"
                      style={{
                        background: active ? "rgba(255,255,255,0.20)" : "rgba(168,85,247,0.14)",
                        color: active ? "#ffffff" : "#7c3aed",
                        letterSpacing: "0.02em",
                      }}
                    >
                      Check-in
                    </span>
                  )}
                </button>
                {b.schedule_url && (
                  <a
                    href={b.schedule_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 pl-2 pr-2.5 h-9 rounded-r-full text-xs font-bold transition"
                    style={{
                      background: active
                        ? "linear-gradient(135deg, #22d3ee 0%, #06b6d4 100%)"
                        : "linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)",
                      color: active ? "#083344" : "#0e7490",
                      border: "1.5px solid " + (active ? "#06b6d4" : "#67e8f9"),
                      borderLeft: "none",
                      textDecoration: "none",
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.background = "linear-gradient(135deg, #cffafe 0%, #a5f3fc 100%)";
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.background = "linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)";
                    }}
                    title={`Open ${b.name} schedule`}
                    data-testid={`study-batch-schedule-${b.id}`}
                  >
                    <CalendarDays size={12} strokeWidth={2.6} />
                    <span className="hidden sm:inline">Schedule</span>
                  </a>
                )}
                </div>
              );
            })}
          </div>
          {batches.some((b) => b.is_checkin_gated && b.checked_in_today === false) && (
            <div
              className="mt-2 text-[11.5px] font-medium inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md"
              style={{ background: "rgba(168,85,247,0.10)", color: "#7c3aed", border: "1px solid rgba(168,85,247,0.35)" }}
              data-testid="study-batch-picker-hint"
            >
              <Lock size={10} /> Batches with a lock need today&apos;s check-in before content unlocks.
            </div>
          )}
        </div>
      )}

      {/* Search */}
      {/* Iter 193.5 — batch-switch loader takes precedence over gate/tree so the
          student sees an inline, centered progress card immediately (no
          fixed overlay, no skeleton flicker). Keeps the batch picker
          rendered above so they can still see which batch is loading. */}
      {batchSwitching ? (
        <StudyLoader active={true} variant="switch" targetName={switchingTo} />
      ) : gatedInfo ? (
        <CheckinGateScreen
          cfg={{}}
          state={{}}
          v2={{}}
          batchName={gatedInfo.batch_name}
          onRefresh={() => load(activeBatchId, { silent: true })}
        />
      ) : (
      <>
      <div className="flex items-center gap-2 h-12 px-4 portal-input mb-5">
        <Search size={14} style={{ color: "var(--px-ink-mute)" }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search chapters, topics, or lectures…"
          className="flex-1 bg-transparent outline-none text-sm" data-testid="study-search" />
        {q && <button onClick={() => setQ("")} aria-label="Clear"><X size={13} style={{ color: "var(--px-ink-mute)" }} /></button>}
      </div>

      {searchResults ? (
        <div className="space-y-2">
          <div className="portal-kicker mb-2">— {searchResults.length} result{searchResults.length === 1 ? "" : "s"}</div>
          {searchResults.length === 0 ? (
            <div className="portal-empty text-sm">No matches for “{q}”.</div>
          ) : searchResults.map((r, i) => (
            <div key={i} className="lms-card p-4 flex items-center justify-between gap-3" data-testid={`study-result-${i}`}>
              <div className="min-w-0">
                <div className="portal-kicker mb-0.5">{r.s?.name} {r.c ? "· " + r.c.name : ""} {r.t ? "· " + r.t.name : ""}</div>
                <div className="text-sm font-semibold" style={{ color: "var(--px-ink)" }}>
                  {r.type === "chapter" ? r.c.name : r.type === "topic" ? r.t.name : r.l.title}
                </div>
              </div>
              {r.type === "lecture" && r.l?.id && (
                <Link to={`/student/lecture/${r.l.id}`}
                  onMouseEnter={prefetchLectureChunk}
                  onTouchStart={prefetchLectureChunk}
                  className="portal-btn portal-btn-primary h-9 shrink-0"><PlayCircle size={13} /> Watch</Link>
              )}
              {r.type === "topic" && r.t?.id && (
                <Link to={`/student/quiz?scope=topic&topic_id=${r.t.id}`} className="portal-btn h-9 shrink-0"><ClipboardList size={12} /> Practice</Link>
              )}
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* Subject tabs as cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-6" data-testid="subject-tabs">
            {tree.map((s) => (
              <button key={s.id}
                onClick={() => { setOpenSubject(s.id); setOpenChapter(null); }}
                className="p-3 rounded-[12px] border transition text-left"
                style={{
                  borderColor: s.id === openSubject ? "var(--accent)" : "var(--px-line)",
                  background: s.id === openSubject ? "var(--accent-soft)" : "var(--px-surface)",
                  boxShadow: "var(--px-shadow-xs)",
                }}
                data-testid={`subject-tab-${s.slug}`}>
                <div className="text-sm font-semibold" style={{ color: s.id === openSubject ? "var(--accent-deep)" : "var(--px-ink)" }}>{s.name}</div>
                <div className="portal-kicker mt-0.5">{s.lecture_count || 0} lectures</div>
              </button>
            ))}
          </div>

          {/* Subject-level quick actions */}
          <div className="flex flex-wrap gap-2 mb-8">
            <Link to={`/student/quiz?scope=subject&subject_id=${subject.id}`}
              className="portal-btn portal-btn-primary h-9 text-xs"
              data-testid={`practice-subject-${subject.slug}`}>
              <ClipboardList size={12} /> Practice {subject.name} MCQs
            </Link>
            <Link to={`/student/quiz?scope=full`}
              className="portal-btn h-9 text-xs"
              data-testid="practice-full-book">
              <ClipboardList size={12} /> Full-book practice
            </Link>
            <Link to={`/student/revision`}
              className="portal-btn h-9 text-xs"
              data-testid="my-revision-deck">
              <ClipboardList size={12} /> My revision deck
            </Link>
          </div>

          {/* Chapters */}
          <div className="space-y-3">
            {(subject.chapters || []).map((ch) => {
              const open = openChapter === ch.id;
              // When EVERY topic under this chapter is locked right now,
              // the chapter-level Practice MCQs button would serve zero
              // MCQs — but the quiz intro still renders with Start + mode
              // pickers, which felt like a bypass ("button clickable even
              // though everything is locked"). Render a non-clickable
              // "Practice locked" pill instead so the lock is visually
              // unambiguous and no request goes out until at least one
              // topic actually unlocks.
              const topics = ch.topics || [];
              const allTopicsLocked = topics.length > 0 && topics.every((t) => !!t.is_locked);
              return (
                <motion.div key={ch.id} layout className="lms-card overflow-hidden">
                  <button
                    onClick={() => setOpenChapter(open ? null : ch.id)}
                    className="w-full flex items-center justify-between p-5 text-left transition"
                    data-testid={`chapter-toggle-${ch.slug}`}
                  >
                    <div>
                      <div className="text-lg font-semibold" style={{ color: "var(--px-ink)" }}>{ch.name}</div>
                      <div className="portal-kicker mt-1">{ch.lecture_count} lectures · {ch.topics?.length || 0} topics</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {allTopicsLocked ? (
                        <span
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center gap-1.5 h-8 px-3 text-[11px] rounded-full font-medium"
                          style={{
                            background: "rgba(229,180,87,0.08)",
                            border: "1.5px dashed rgba(229,180,87,0.65)",
                            color: "#9a6d11",
                            cursor: "not-allowed",
                          }}
                          title="All topics in this chapter are locked for your batch"
                          data-testid={`practice-chapter-locked-${ch.slug}`}
                        >
                          <Lock size={11} /> Practice locked
                        </span>
                      ) : (
                        <Link
                          to={`/student/quiz?scope=chapter&chapter_id=${ch.id}`}
                          onClick={(e) => e.stopPropagation()}
                          className="portal-btn h-8 px-3 text-[11px]"
                          data-testid={`practice-chapter-${ch.slug}`}
                        >
                          <ClipboardList size={11} /> Practice MCQs
                        </Link>
                      )}
                      <ChevronRight size={18} style={{ color: "var(--px-ink-mute)", transform: open ? "rotate(90deg)" : "none", transition: "transform 160ms" }} />
                    </div>
                  </button>
                  {open && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="px-5 pb-5 space-y-4 border-t" style={{ borderColor: "var(--px-line)" }}>
                      {(ch.topics || []).map((t) => {
                        const isTest = t.kind === "test";
                        const topicLocked = !!t.is_locked;
                        if (isTest) {
                          // Test-only topic — single CTA card, no lecture grid.
                          // If locked, render a non-clickable card with a countdown.
                          if (topicLocked) {
                            return (
                              <div key={t.id} className="pt-4" data-testid={`topic-test-locked-${t.id}`}>
                                <div
                                  className="flex items-center gap-4 p-4 rounded-[14px] border opacity-90"
                                  style={{
                                    background: "linear-gradient(135deg, rgba(229,180,87,0.10), rgba(229,180,87,0.02))",
                                    borderColor: "rgba(229,180,87,0.45)",
                                  }}
                                >
                                  <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                                    style={{ background: "rgba(229,180,87,0.18)", border: "1px solid rgba(229,180,87,0.6)" }}>
                                    <Lock size={16} style={{ color: "#9a6d11" }} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                      <span className="text-[10px] font-mono font-bold uppercase tracking-[0.14em]" style={{ color: "#9a6d11" }}>Test · Locked</span>
                                      <UnlockCountdown availableFrom={t.available_from} />
                                    </div>
                                    <div className="text-[15px] font-semibold truncate" style={{ color: "var(--px-ink)" }}>{t.name}</div>
                                  </div>
                                  <span className="portal-btn h-9 px-4 text-[12px] shrink-0" style={{ background: "transparent", color: "#9a6d11", border: "1.5px dashed #9a6d11", cursor: "not-allowed" }} title="Available after the unlock time">
                                    <Lock size={11} /> Locked
                                  </span>
                                </div>
                              </div>
                            );
                          }
                          return (
                            <div key={t.id} className="pt-4" data-testid={`topic-test-${t.id}`}>
                              <Link
                                to={`/student/quiz?scope=topic&topic_id=${t.id}&mode=test`}
                                className="group flex items-center gap-4 p-4 rounded-[14px] border transition"
                                style={{
                                  background: "linear-gradient(135deg, rgba(212,166,74,0.08), rgba(212,166,74,0.02))",
                                  borderColor: "rgba(212,166,74,0.45)",
                                }}
                                data-testid={`take-test-${t.id}`}
                              >
                                <div className="w-11 h-11 rounded-full flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                                  style={{ background: "rgba(212,166,74,0.15)", border: "1px solid rgba(212,166,74,0.6)" }}>
                                  <ClipboardList size={18} style={{ color: "#a8801f" }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-0.5">
                                    <span className="text-[10px] font-mono font-bold uppercase tracking-[0.14em]" style={{ color: "#a8801f" }}>
                                      Test
                                    </span>
                                    {t.default_total_seconds && (
                                      <span className="text-[10px] font-mono" style={{ color: "var(--px-ink-mute)" }}>
                                        · {Math.round(t.default_total_seconds / 60)} min
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[15px] font-semibold truncate" style={{ color: "var(--px-ink)" }}>
                                    {t.name}
                                  </div>
                                </div>
                                <span className="portal-btn portal-btn-primary h-9 px-4 text-[12px] shrink-0">
                                  Take test <ChevronRight size={13} />
                                </span>
                              </Link>
                            </div>
                          );
                        }
                        // Regular learning topic — lectures grid + practice CTA.
                        return (
                        <div key={t.id} className="pt-4">
                          <div className="flex items-center justify-between mb-2 gap-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="text-sm font-semibold" style={{ color: "var(--px-ink)" }}>{t.name}</div>
                              {topicLocked && <UnlockCountdown availableFrom={t.available_from} kicker="Test locked" />}
                            </div>
                            {topicLocked ? (
                              <span className="portal-btn h-7 px-3 text-[11px] shrink-0" style={{ background: "transparent", color: "var(--px-warn)", border: "1.5px dashed var(--px-warn)", cursor: "not-allowed" }} title="Practice unlocks with the topic">
                                <Lock size={11} /> Practice locked
                              </span>
                            ) : (
                              <Link
                                to={`/student/quiz?scope=topic&topic_id=${t.id}`}
                                className="portal-btn h-7 px-3 text-[11px] shrink-0"
                                data-testid={`practice-mcqs-${t.id}`}
                              >
                                <ClipboardList size={11} /> Practice MCQs
                              </Link>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {(t.lectures || []).length === 0 ? (
                              <div className="text-xs italic" style={{ color: "var(--px-ink-mute)" }}>No lectures yet</div>
                            ) : (t.lectures || []).map((lec) => {
                              // INDEPENDENT scheduling: lecture lock follows
                              // the LECTURE's own schedule only — NOT the
                              // parent topic's schedule. Locking a topic
                              // affects MCQs/test for that topic; lectures
                              // inside have their own schedule per admin.
                              const lecLocked = !!lec.is_locked;
                              if (lecLocked) {
                                return (
                                  <div key={lec.id}
                                    className="flex items-center gap-3 p-3 rounded-[12px] border opacity-90"
                                    style={{ background: "var(--px-paper-soft)", borderColor: "var(--px-warn)", cursor: "not-allowed" }}
                                    data-testid={`lecture-locked-${lec.id}`}>
                                    <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                                      style={{ background: "var(--px-warn-soft)", border: "1px solid var(--px-warn)" }}>
                                      <Lock size={14} style={{ color: "var(--px-warn)" }} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm truncate" style={{ color: "var(--px-ink)" }}>{lec.title}</div>
                                      <UnlockCountdown availableFrom={lec.available_from} />
                                    </div>
                                  </div>
                                );
                              }
                              return (
                                <Link key={lec.id} to={`/student/lecture/${lec.id}`}
                                  onMouseEnter={prefetchLectureChunk}
                                  onTouchStart={prefetchLectureChunk}
                                  className="group flex items-center gap-3 p-3 rounded-[12px] border transition"
                                  style={{ background: "var(--px-paper-soft)", borderColor: "var(--px-line)" }}
                                  data-testid={`lecture-link-${lec.id}`}>
                                  <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 transition-transform group-hover:scale-110"
                                    style={{ background: "var(--accent-soft)", border: "1px solid var(--accent)" }}>
                                    <PlayCircle size={16} style={{ color: "var(--accent-deep)" }} />
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm truncate" style={{ color: "var(--px-ink)" }}>{lec.title}</div>
                                    {lec.duration_sec && (
                                      <div className="text-[10px] flex items-center gap-1 mt-0.5" style={{ color: "var(--px-ink-mute)" }}>
                                        <Clock size={10} /> {Math.round(lec.duration_sec/60)} min
                                      </div>
                                    )}
                                  </div>
                                </Link>
                              );
                            })}
                          </div>
                        </div>
                        );
                      })}
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </>
      )}
      </>
      )}
    </div>
    </BatchAccessGate>
  );
}


/* ─────────────── UnlockCountdown ───────────────
   Live ticking "Unlocks in 2h 14m" badge for soft-locked content. Refreshes
   every 30s — granular enough to feel live, not so fast that the React
   reconciler thrashes. When the unlock moment passes, the parent's content-
   tree fetch will re-run on next visit and the lock icon will disappear. */
function UnlockCountdown({ availableFrom, kicker = "Unlocks" }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((v) => v + 1), 30 * 1000);
    return () => clearInterval(t);
  }, []);
  if (!availableFrom) return null;
  const ms = new Date(availableFrom).getTime() - Date.now();
  if (isNaN(ms)) return null;
  if (ms <= 0) return null;
  const min = Math.floor(ms / 60000);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  let label;
  if (day > 0) label = `${day}d ${hr % 24}h`;
  else if (hr > 0) label = `${hr}h ${min % 60}m`;
  else label = `${min}m`;
  const exact = new Date(availableFrom).toLocaleString();
  return (
    <span
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-[4px] text-[10px] font-mono font-bold"
      style={{ background: "var(--px-warn-soft)", color: "var(--px-warn)", border: "1px solid var(--px-warn)" }}
      title={`Unlocks at ${exact}`}
      data-testid="unlock-countdown"
    >
      <Clock size={9} /> {kicker} in {label}
    </span>
  );
}